import { useState, useEffect, useCallback, Dispatch, SetStateAction } from 'react';
import { Client, Transfer, Item, Sale, Settings, VendorPayment, ClientPayment } from '@/types';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';

const defaultSettings: Settings = {
  myCommissionRate: 2.5,
  providerCommissionRate: 1.5,
  currency: 'CAD',
  businessName: 'BizFlow',
};

// ─── Clients ────────────────────────────────────────────────────────────────

function rowToClient(r: Record<string, unknown>): Client {
  return {
    id: r.id as string,
    name: r.name as string,
    phone: (r.phone as string) ?? '',
    email: (r.email as string) ?? '',
    totalTransferred: Number(r.total_transferred ?? 0),
    totalMyCommission: Number(r.total_my_commission ?? 0),
    totalProviderCommission: Number(r.total_provider_commission ?? 0),
    balance: Number(r.balance ?? 0),
    settledAmount: Number(r.settled_amount ?? 0),
    createdAt: (r.created_at as string) ?? '',
  };
}

function clientToRow(c: Client) {
  return {
    id: c.id,
    name: c.name,
    phone: c.phone,
    email: c.email,
    total_transferred: c.totalTransferred,
    total_my_commission: c.totalMyCommission,
    total_provider_commission: c.totalProviderCommission,
    balance: c.balance,
    settled_amount: c.settledAmount,
    created_at: c.createdAt,
  };
}

export function useClients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured) { setLoading(false); return; }
    supabase.from('clients').select('*').order('created_at', { ascending: true }).then(({ data, error }) => {
      if (error) console.error('clients fetch error:', error);
      if (data) setClients(data.map(r => rowToClient(r as Record<string, unknown>)));
      setLoading(false);
    });
  }, []);

  const addClient = useCallback(async (client: Client) => {
    // Optimistic update first
    setClients(prev => [...prev, client]);
    const { error } = await supabase.from('clients').insert(clientToRow(client));
    if (error) console.error('addClient error:', error);
  }, []);

  const updateClient = useCallback(async (client: Client) => {
    setClients(prev => prev.map(c => c.id === client.id ? client : c));
    const { error } = await supabase.from('clients').update(clientToRow(client)).eq('id', client.id);
    if (error) console.error('updateClient error:', error);
  }, []);

  const deleteClient = useCallback(async (id: string) => {
    setClients(prev => prev.filter(c => c.id !== id));
    const { error } = await supabase.from('clients').delete().eq('id', id);
    if (error) console.error('deleteClient error:', error);
  }, []);

  const save = useCallback((updated: Client[]) => setClients(updated), []);

  return { clients, loading, addClient, updateClient, deleteClient, setClients: save };
}

// ─── Transfers ───────────────────────────────────────────────────────────────

function rowToTransfer(r: Record<string, unknown>): Transfer {
  return {
    id: r.id as string,
    date: r.date as string,
    clientId: (r.client_id as string) ?? '',
    clientName: (r.client_name as string) ?? '',
    transactionId: (r.transaction_id as string) ?? '',
    amount: Number(r.amount ?? 0),
    type: (r.type as Transfer['type']) ?? 'client_paid_me',
    myCommissionRate: Number(r.my_commission_rate ?? 0),
    providerCommissionRate: Number(r.provider_commission_rate ?? 0),
    myCommission: Number(r.my_commission ?? 0),
    providerCommission: Number(r.provider_commission ?? 0),
    myProfit: Number(r.my_profit ?? 0),
    totalWithMyCommission: Number(r.total_with_my_commission ?? 0),
    totalWithProviderCommission: Number(r.total_with_provider_commission ?? 0),
    netAmountDue: Number(r.net_amount_due ?? 0),
    status: (r.status as Transfer['status']) ?? 'pending',
    isSettled: Boolean(r.is_settled),
    notes: (r.notes as string) ?? '',
  };
}

function transferToRow(t: Transfer) {
  return {
    id: t.id,
    date: t.date,
    client_id: t.clientId,
    client_name: t.clientName,
    transaction_id: t.transactionId,
    amount: t.amount,
    type: t.type,
    my_commission_rate: t.myCommissionRate,
    provider_commission_rate: t.providerCommissionRate,
    my_commission: t.myCommission,
    provider_commission: t.providerCommission,
    my_profit: t.myProfit,
    total_with_my_commission: t.totalWithMyCommission,
    total_with_provider_commission: t.totalWithProviderCommission,
    net_amount_due: t.netAmountDue,
    status: t.status,
    is_settled: t.isSettled,
    notes: t.notes,
  };
}

export function useTransfers() {
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured) { setLoading(false); return; }
    supabase.from('transfers').select('*').order('date', { ascending: false }).then(({ data, error }) => {
      if (error) console.error('transfers fetch error:', error);
      if (data) setTransfers(data.map(r => rowToTransfer(r as Record<string, unknown>)));
      setLoading(false);
    });
  }, []);

  const addTransfer = useCallback(async (transfer: Transfer) => {
    setTransfers(prev => [transfer, ...prev]);
    const { error } = await supabase.from('transfers').insert(transferToRow(transfer));
    if (error) console.error('addTransfer error:', error);
  }, []);

  const updateTransfer = useCallback(async (transfer: Transfer) => {
    setTransfers(prev => prev.map(t => t.id === transfer.id ? transfer : t));
    const { error } = await supabase.from('transfers').update(transferToRow(transfer)).eq('id', transfer.id);
    if (error) console.error('updateTransfer error:', error);
  }, []);

  const deleteTransfer = useCallback(async (id: string) => {
    setTransfers(prev => prev.filter(t => t.id !== id));
    const { error } = await supabase.from('transfers').delete().eq('id', id);
    if (error) console.error('deleteTransfer error:', error);
  }, []);

  const save = useCallback((updated: Transfer[]) => setTransfers(updated), []);

  return { transfers, loading, addTransfer, updateTransfer, deleteTransfer, setTransfers: save };
}

// ─── Items ───────────────────────────────────────────────────────────────────

function rowToItem(r: Record<string, unknown>): Item {
  return {
    id: r.id as string,
    name: r.name as string,
    sku: (r.sku as string) ?? '',
    category: (r.category as string) ?? '',
    purchasePrice: Number(r.purchase_price ?? 0),
    quantityPurchased: Number(r.quantity_purchased ?? 0),
    totalPurchaseValue: Number(r.total_purchase_value ?? 0),
    currentStock: Number(r.current_stock ?? 0),
    lowStockAlert: Number(r.low_stock_alert ?? 0),
    supplier: (r.supplier as string) ?? '',
    purchaseDate: (r.purchase_date as string) ?? '',
  };
}

function itemToRow(i: Item) {
  return {
    id: i.id,
    name: i.name,
    sku: i.sku,
    category: i.category,
    purchase_price: i.purchasePrice,
    quantity_purchased: i.quantityPurchased,
    total_purchase_value: i.totalPurchaseValue,
    current_stock: i.currentStock,
    low_stock_alert: i.lowStockAlert,
    supplier: i.supplier,
    purchase_date: i.purchaseDate,
  };
}

export function useItems() {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured) { setLoading(false); return; }
    supabase.from('items').select('*').order('name', { ascending: true }).then(({ data, error }) => {
      if (error) console.error('items fetch error:', error);
      if (data) setItems(data.map(r => rowToItem(r as Record<string, unknown>)));
      setLoading(false);
    });
  }, []);

  const addItem = useCallback(async (item: Item) => {
    setItems(prev => [...prev, item]);
    const { error } = await supabase.from('items').insert(itemToRow(item));
    if (error) console.error('addItem error:', error);
  }, []);

  const updateItem = useCallback(async (item: Item) => {
    setItems(prev => prev.map(i => i.id === item.id ? item : i));
    const { error } = await supabase.from('items').update(itemToRow(item)).eq('id', item.id);
    if (error) console.error('updateItem error:', error);
  }, []);

  const deleteItem = useCallback(async (id: string) => {
    setItems(prev => prev.filter(i => i.id !== id));
    const { error } = await supabase.from('items').delete().eq('id', id);
    if (error) console.error('deleteItem error:', error);
  }, []);

  const save = useCallback((updated: Item[]) => setItems(updated), []);

  return { items, loading, addItem, updateItem, deleteItem, setItems: save, setItemsDispatch: setItems };
}

// ─── Sales ───────────────────────────────────────────────────────────────────

function rowToSale(r: Record<string, unknown>): Sale {
  return {
    id: r.id as string,
    date: r.date as string,
    customerName: (r.customer_name as string) ?? '',
    itemId: (r.item_id as string) ?? '',
    itemName: (r.item_name as string) ?? '',
    quantitySold: Number(r.quantity_sold ?? 0),
    sellingPrice: Number(r.selling_price ?? 0),
    discount: Number(r.discount ?? 0),
    paymentStatus: (r.payment_status as Sale['paymentStatus']) ?? 'pending',
    notes: (r.notes as string) ?? '',
    cogs: Number(r.cogs ?? 0),
    grossProfit: Number(r.gross_profit ?? 0),
    netProfit: Number(r.net_profit ?? 0),
    totalSaleValue: Number(r.total_sale_value ?? 0),
  };
}

function saleToRow(s: Sale) {
  return {
    id: s.id,
    date: s.date,
    customer_name: s.customerName,
    item_id: s.itemId,
    item_name: s.itemName,
    quantity_sold: s.quantitySold,
    selling_price: s.sellingPrice,
    discount: s.discount,
    payment_status: s.paymentStatus,
    notes: s.notes,
    cogs: s.cogs,
    gross_profit: s.grossProfit,
    net_profit: s.netProfit,
    total_sale_value: s.totalSaleValue,
  };
}

/** Adjust an item's current_stock by `delta` (negative = deduct, positive = restore) */
async function adjustItemStock(
  itemId: string,
  delta: number,
  setItems: Dispatch<SetStateAction<Item[]>>,
) {
  if (!itemId || delta === 0) return;
  // Optimistic UI update
  setItems(prev =>
    prev.map(i =>
      i.id === itemId
        ? { ...i, currentStock: Math.max(0, i.currentStock + delta) }
        : i,
    ),
  );
  // Persist to Supabase using RPC-style increment
  const { data: current } = await supabase
    .from('items')
    .select('current_stock')
    .eq('id', itemId)
    .maybeSingle();
  if (current) {
    const newStock = Math.max(0, Number(current.current_stock) + delta);
    const { error } = await supabase
      .from('items')
      .update({ current_stock: newStock })
      .eq('id', itemId);
    if (error) console.error('adjustItemStock error:', error);
  }
}

export function useSales() {
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured) { setLoading(false); return; }
    supabase.from('sales').select('*').order('date', { ascending: false }).then(({ data, error }) => {
      if (error) console.error('sales fetch error:', error);
      if (data) setSales(data.map(r => rowToSale(r as Record<string, unknown>)));
      setLoading(false);
    });
  }, []);

  const addSale = useCallback(async (sale: Sale, updateItemsFn?: Dispatch<SetStateAction<Item[]>>) => {
    setSales(prev => [sale, ...prev]);
    const { error } = await supabase.from('sales').insert(saleToRow(sale));
    if (error) console.error('addSale error:', error);
    // Deduct stock
    if (updateItemsFn && sale.itemId) {
      await adjustItemStock(sale.itemId, -sale.quantitySold, updateItemsFn);
    }
  }, []);

  const updateSale = useCallback(async (sale: Sale, oldSale?: Sale, updateItemsFn?: Dispatch<SetStateAction<Item[]>>) => {
    setSales(prev => prev.map(s => s.id === sale.id ? sale : s));
    const { error } = await supabase.from('sales').update(saleToRow(sale)).eq('id', sale.id);
    if (error) console.error('updateSale error:', error);
    // Adjust stock: restore old qty, deduct new qty
    if (updateItemsFn && oldSale) {
      if (oldSale.itemId && oldSale.itemId !== sale.itemId) {
        // Item changed: restore old item stock fully
        await adjustItemStock(oldSale.itemId, oldSale.quantitySold, updateItemsFn);
        // Deduct from new item
        await adjustItemStock(sale.itemId, -sale.quantitySold, updateItemsFn);
      } else if (sale.itemId) {
        // Same item, quantity may have changed
        const diff = oldSale.quantitySold - sale.quantitySold;
        await adjustItemStock(sale.itemId, diff, updateItemsFn);
      }
    }
  }, []);

  const deleteSale = useCallback(async (id: string, updateItemsFn?: Dispatch<SetStateAction<Item[]>>) => {
    const saleToDelete = sales.find(s => s.id === id);
    setSales(prev => prev.filter(s => s.id !== id));
    const { error } = await supabase.from('sales').delete().eq('id', id);
    if (error) console.error('deleteSale error:', error);
    // Restore stock
    if (updateItemsFn && saleToDelete?.itemId) {
      await adjustItemStock(saleToDelete.itemId, saleToDelete.quantitySold, updateItemsFn);
    }
  }, [sales]);

  const save = useCallback((updated: Sale[]) => setSales(updated), []);

  return { sales, loading, addSale, updateSale, deleteSale, setSales: save };
}

// ─── Settings ────────────────────────────────────────────────────────────────

export function useSettings() {
  const [settings, setSettingsState] = useState<Settings>(defaultSettings);

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    supabase.from('settings').select('*').eq('id', 1).maybeSingle().then(({ data, error }) => {
      if (error) console.error('settings fetch error:', error);
      if (data) {
        setSettingsState({
          myCommissionRate: Number(data.my_commission_rate ?? 2.5),
          providerCommissionRate: Number(data.provider_commission_rate ?? 1.5),
          currency: (data.currency as string) ?? 'CAD',
          businessName: (data.business_name as string) ?? 'BizFlow',
        });
      } else {
        supabase.from('settings').insert({ id: 1, my_commission_rate: 2.5, provider_commission_rate: 1.5, currency: 'CAD', business_name: 'BizFlow' });
      }
    });
  }, []);

  const updateSettings = useCallback(async (updated: Settings) => {
    setSettingsState(updated);
    const { error } = await supabase.from('settings').upsert({
      id: 1,
      my_commission_rate: updated.myCommissionRate,
      provider_commission_rate: updated.providerCommissionRate,
      currency: updated.currency,
      business_name: updated.businessName,
    });
    if (error) console.error('updateSettings error:', error);
  }, []);

  return { settings, updateSettings };
}

// ─── Vendor Payments ─────────────────────────────────────────────────────────

function rowToVendorPayment(r: Record<string, unknown>): VendorPayment {
  return {
    id: r.id as string,
    date: r.date as string,
    vendorName: (r.vendor_name as string) ?? '',
    amount: Number(r.amount ?? 0),
    referenceNote: (r.reference_note as string) ?? '',
    itemId: (r.item_id as string) ?? '',
    itemName: (r.item_name as string) ?? '',
  };
}

function vendorPaymentToRow(p: VendorPayment) {
  return {
    id: p.id,
    date: p.date,
    vendor_name: p.vendorName,
    amount: p.amount,
    reference_note: p.referenceNote,
    item_id: p.itemId ?? '',
    item_name: p.itemName ?? '',
  };
}

export function useVendorPayments() {
  const [vendorPayments, setVendorPaymentsState] = useState<VendorPayment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured) { setLoading(false); return; }
    supabase.from('vendor_payments').select('*').order('date', { ascending: false }).then(({ data, error }) => {
      if (error) console.error('vendor_payments fetch error:', error);
      if (data) setVendorPaymentsState(data.map(r => rowToVendorPayment(r as Record<string, unknown>)));
      setLoading(false);
    });
  }, []);

  const addVendorPayment = useCallback(async (payment: VendorPayment) => {
    setVendorPaymentsState(prev => [payment, ...prev]);
    const { error } = await supabase.from('vendor_payments').insert(vendorPaymentToRow(payment));
    if (error) console.error('addVendorPayment error:', error);
  }, []);

  const deleteVendorPayment = useCallback(async (id: string) => {
    setVendorPaymentsState(prev => prev.filter(p => p.id !== id));
    const { error } = await supabase.from('vendor_payments').delete().eq('id', id);
    if (error) console.error('deleteVendorPayment error:', error);
  }, []);

  const save = useCallback((updated: VendorPayment[]) => setVendorPaymentsState(updated), []);

  return { vendorPayments, loading, addVendorPayment, deleteVendorPayment, setVendorPayments: save };
}

// ─── Client Payments ─────────────────────────────────────────────────────────

function rowToClientPayment(r: Record<string, unknown>): ClientPayment {
  return {
    id: r.id as string,
    date: r.date as string,
    clientId: (r.client_id as string) ?? '',
    clientName: (r.client_name as string) ?? '',
    transferId: (r.transfer_id as string) ?? '',
    transferTxnId: (r.transfer_txn_id as string) ?? '',
    amountReceived: Number(r.amount_received ?? 0),
    notes: (r.notes as string) ?? '',
  };
}

function clientPaymentToRow(p: ClientPayment) {
  return {
    id: p.id,
    date: p.date,
    client_id: p.clientId,
    client_name: p.clientName,
    transfer_id: p.transferId,
    transfer_txn_id: p.transferTxnId,
    amount_received: p.amountReceived,
    notes: p.notes,
  };
}

export function useClientPayments() {
  const [clientPayments, setClientPaymentsState] = useState<ClientPayment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured) { setLoading(false); return; }
    supabase.from('client_payments').select('*').order('date', { ascending: false }).then(({ data, error }) => {
      if (error) console.error('client_payments fetch error:', error);
      if (data) setClientPaymentsState(data.map(r => rowToClientPayment(r as Record<string, unknown>)));
      setLoading(false);
    });
  }, []);

  const addClientPayment = useCallback(async (payment: ClientPayment) => {
    setClientPaymentsState(prev => [payment, ...prev]);
    const { error } = await supabase.from('client_payments').insert(clientPaymentToRow(payment));
    if (error) console.error('addClientPayment error:', error);
  }, []);

  const deleteClientPayment = useCallback(async (id: string) => {
    setClientPaymentsState(prev => prev.filter(p => p.id !== id));
    const { error } = await supabase.from('client_payments').delete().eq('id', id);
    if (error) console.error('deleteClientPayment error:', error);
  }, []);

  const save = useCallback((updated: ClientPayment[]) => setClientPaymentsState(updated), []);

  return { clientPayments, loading, addClientPayment, deleteClientPayment, setClientPayments: save };
}

// ─── Theme ───────────────────────────────────────────────────────────────────

export function useTheme() {
  const [isDark, setIsDark] = useState<boolean>(() => {
    const stored = localStorage.getItem('bizflow_theme');
    return stored ? stored === 'dark' : false;
  });

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('bizflow_theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const toggleTheme = useCallback(() => setIsDark(prev => !prev), []);

  return { isDark, toggleTheme };
}
